import React, { useMemo, useState } from "react";
import dropdownIcon from "../../../assets/componentsIcons/dropdown/dropdown.svg";
import editIcon from "../../../assets/componentsIcons/dropdown/edit.svg";
import UtilHelper from "../../../utils/UtilHelper";
import ActionMenu from "../../../newComponents/actionMenus/action-menu";
import LargeTooltip from "../../../newComponents/tooltips/large-tooltip";
import { GetImages } from "../../../utils/GetImages";
import { usePermissions } from "../../../hooks/usePermissions";

interface Props {
  listingData: any;
  onEdit: () => void;
}

const FEEDBACK_DESCRIPTION_TRUNCATE_LENGTH = 30;

const FeedbackCardItem: React.FC<Props> = ({ listingData, onEdit }) => {
  const check = usePermissions();
  const canWrite = check.canWrite("Feedback");

  const [tooltipState, setTooltipState] = useState<{
    isOpen: boolean;
    description: string;
    position: { top: number; left: number };
    title?: string;
  }>({
    isOpen: false,
    description: "",
    position: { top: 0, left: 0 },
    title: "Description",
  });

  const handleViewMoreClick = (
    event: React.MouseEvent<HTMLElement>,
    description: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipState({
      isOpen: true,
      description,
      position: {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      },
    });
  };

  const handleCloseTooltip = () => {
    setTooltipState((prev) => ({ ...prev, isOpen: false }));
  };

  // Feedback descriptions are organization-user-authored free text stored raw by
  // the backend. LargeTooltip renders a *string* description through
  // dangerouslySetInnerHTML, so it is passed as a ReactNode instead — React then
  // escapes it as text and the markup can never execute in the reader's session.
  // The useMemo is load-bearing, not just an optimization: LargeTooltip's
  // useLayoutEffect lists `description` in its dependency array and calls
  // setAdjustedStyle with a fresh object, so an inline <>{...}</> — a new node
  // identity every render — would re-trigger the effect forever. Do not inline it.
  const tooltipDescriptionNode = useMemo(
    () => <>{tooltipState.description}</>,
    [tooltipState.description]
  );

  const menuItems = [
    { icon: editIcon, label: "Edit", action: onEdit },
  ];

  return (
    <div className="listing-card organisation-listing-card">
      <LargeTooltip
        isOpen={tooltipState.isOpen}
        onClose={handleCloseTooltip}
        description={tooltipDescriptionNode}
        position={tooltipState.position}
        title={tooltipState.title}
      />
      <div className="listing-card-header">
        <div className="tenant-avatar">
          <div className="tenant-initials">
            {UtilHelper.getInitials(listingData?.formatted?.FeedbackTypeId || "FB")}
          </div>
        </div>

        <div className="listing-details">
          <div className="listing-info-container">
            <div className="listing-info">
              <h3 className="listing-address">{listingData?.formatted?.FeedbackTypeId || "Feedback"}</h3>
              <p className="listing-type feedback-card-description mt-2">
                {listingData?.feedbackDescription?.length > FEEDBACK_DESCRIPTION_TRUNCATE_LENGTH
                  ? `${listingData.feedbackDescription.slice(0, FEEDBACK_DESCRIPTION_TRUNCATE_LENGTH)}...`
                  : listingData?.feedbackDescription}
                {listingData?.feedbackDescription?.length > FEEDBACK_DESCRIPTION_TRUNCATE_LENGTH && (
                  <span
                    className="listing-viewmore mx-2 ml-1 text-blue-500 underline cursor-pointer"
                    onClick={(e) => handleViewMoreClick(e, listingData.feedbackDescription)}
                  >
                    View more
                  </span>
                )}
              </p>
              <div className="feedback-card-date d-flex align-items-center">
                <img src={GetImages.calendarIcon} alt="info" width={20} height={20} className="stat-icon me-1" />
                {listingData?.formatted?.CreatedDateString}
              </div>
            </div>
          </div>
        </div>

        <div className="listing-progress-wrapper">
          <span className={`listing-status-badge ${listingData?.formatted?.FeedbackStatus?.toLowerCase()}`}>
            {listingData?.formatted?.FeedbackStatus}
          </span>
        </div>

        {canWrite && (
        <ActionMenu
          trigger={<img src={dropdownIcon} alt="Menu" width={26} height={26} className="iconStyleWithWhiteFilter"/>}
          menuItems={menuItems}
          Id={listingData.id}
        />
        )}
      </div>
    </div>
  );
};

export default FeedbackCardItem;